import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { DeviceStatusStation } from './DeviceStatusStation';
import { DeviceStatus } from './DeviceStatus';
import { StationAlias } from './StationAlias';
import type { EntityState } from './../enums/EntityState';

export class StationDeviceStatus extends BaseObj {
    public Conflicts: DeviceStatusStation[];

    public DeviceStatus: DeviceStatus | null;
    public __devicestatus: DeviceStatus | null;

    public Station: DeviceStatusStation | null;
    public __station: DeviceStatusStation | null;

    public readonly StationAlias: StationAlias | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Conflicts?: DeviceStatusStation[];
        DeviceStatus?: DeviceStatus | null;
        EntityState?: EntityState;
        Station?: DeviceStatusStation | null;
        StationAlias?: StationAlias | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StationDeviceStatus>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StationDeviceStatus tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Conflicts = values.Conflicts ?? [];
        this.Conflicts = values.Conflicts ?? [];

        this.DeviceStatus = values.DeviceStatus ?? null;
        this.__devicestatus = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.DeviceStatus ?? null,
            childField: 'DeviceStatus',
        });
        Object.defineProperty(this, 'DeviceStatus', {
            get(): DeviceStatus | null {
                return this.__devicestatus;
            },
            set(value: DeviceStatus | null) {
                this.__devicestatus = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'DeviceStatus',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.Station = values.Station ?? null;
        this.__station = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.Station ?? null,
            childField: 'Station',
        });
        Object.defineProperty(this, 'Station', {
            get(): DeviceStatusStation | null {
                return this.__station;
            },
            set(value: DeviceStatusStation | null) {
                this.__station = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'Station',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.StationAlias = values.StationAlias ?? null;
        this.StationAlias = values.StationAlias ?? null;
        this.StationAlias?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('StationAlias', this.StationAlias),
        );
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"Conflicts": [ ${this.Conflicts.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"DeviceStatus": ${!this.DeviceStatus ? null : this.DeviceStatus?.serialize()}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"Station": ${!this.Station ? null : this.Station?.serialize()}` +
            ',' +
            `"StationAlias": ${!this.StationAlias ? null : this.StationAlias?.serialize()}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StationDeviceStatus from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Conflicts?: DeviceStatusStation[];
        DeviceStatus?: DeviceStatus | null;
        Station?: DeviceStatusStation | null;
        StationAlias?: StationAlias | null;
        EntityState?: 'Added' | 'Unchanged';
    }): StationDeviceStatus {
        return new StationDeviceStatus(values);
    }

    public static _FromServerJson(json: any): StationDeviceStatus {
        return new StationDeviceStatus({
            Conflicts: json.Conflicts?.map((x: any) => DeviceStatusStation._FromServerJson(x)),
            DeviceStatus: json.DeviceStatus ? DeviceStatus._FromServerJson(json.DeviceStatus) : null,
            Station: json.Station ? DeviceStatusStation._FromServerJson(json.Station) : null,
            StationAlias: json.StationAlias ? StationAlias._FromServerJson(json.StationAlias) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StationDeviceStatus from another instance of StationDeviceStatus.
     * @param other The StationDeviceStatus you want to clone
     * @returns A cloned StationDeviceStatus
     */
    public static FromOther(other: StationDeviceStatus): StationDeviceStatus {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StationDeviceStatus._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StationDeviceStatus.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('Conflicts' | 'DeviceStatus' | 'Station' | 'StationAlias')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StationDeviceStatus';
}
